import { Laptop } from '@/types/models';

export interface GroupedLaptop {
  key: string;
  brand: string;
  model: string;
  cpu: string | null;
  gpu?: string | null;
  gpuMixed: boolean; // true when units in this group have differing GPU values
  screen_size?: string | null;
  touchscreen?: boolean | null;
  units: Laptop[];
  totalQuantity: number;
  inStockQuantity: number;
  availableQuantity: number; // in_stock or reserved
  minPrice: number;
  maxPrice: number;
  minCost?: number;
  maxCost?: number;
  hasArchived: boolean;
}

/**
 * Normalizes base machine identity key.
 * Strictly excludes mutable attributes like RAM, Storage, Serial Number, Stock ID, and Condition.
 * Includes fixed base hardware: Brand, Model, CPU, and Touchscreen hardware capability.
 * GPU is intentionally NOT in the group key — it is a separate discrete GPU field visible per-unit.
 */
export function getLaptopGroupKey(laptop: {
  brand?: string | null;
  model?: string | null;
  cpu?: string | null;
  touchscreen?: boolean | null;
}): string {
  const brand = (laptop.brand || '').trim().toLowerCase();
  const model = (laptop.model || '').trim().toLowerCase();
  const cpu = (laptop.cpu || '').trim().toLowerCase();
  const touch = laptop.touchscreen === true ? 'touch' : laptop.touchscreen === false ? 'nontouch' : 'unknown';
  return `${brand}::${model}::${cpu}::${touch}`;
}

/**
 * Groups an array of serialized physical laptops into base-machine presentation groups.
 */
export function groupLaptops(laptops: Laptop[]): GroupedLaptop[] {
  const groupMap = new Map<string, GroupedLaptop & { _gpuSet: Set<string> }>();

  for (const laptop of laptops) {
    const key = getLaptopGroupKey(laptop);
    const existing = groupMap.get(key);

    const askingPrice = laptop.asking_price ?? 0;
    const cost = laptop.landed_cost ?? laptop.purchase_price ?? 0;
    const gpuNorm = (laptop.gpu || '').trim().toLowerCase();

    if (!existing) {
      const gpuSet = new Set<string>();
      gpuSet.add(gpuNorm);
      groupMap.set(key, {
        key,
        brand: laptop.brand,
        model: laptop.model,
        cpu: laptop.cpu || null,
        gpu: laptop.gpu || null,
        gpuMixed: false,
        screen_size: laptop.screen_size || null,
        touchscreen: laptop.touchscreen ?? null,
        units: [laptop],
        totalQuantity: 1,
        inStockQuantity: laptop.status === 'in_stock' ? 1 : 0,
        availableQuantity: ['in_stock', 'reserved'].includes(laptop.status) ? 1 : 0,
        minPrice: askingPrice,
        maxPrice: askingPrice,
        minCost: cost,
        maxCost: cost,
        hasArchived: !!laptop.archived_at,
        _gpuSet: gpuSet,
      });
    } else {
      existing.units.push(laptop);
      existing.totalQuantity += 1;
      if (laptop.status === 'in_stock') existing.inStockQuantity += 1;
      if (['in_stock', 'reserved'].includes(laptop.status)) existing.availableQuantity += 1;
      existing.minPrice = Math.min(existing.minPrice, askingPrice);
      existing.maxPrice = Math.max(existing.maxPrice, askingPrice);
      if (cost > 0) {
        existing.minCost = existing.minCost ? Math.min(existing.minCost, cost) : cost;
        existing.maxCost = existing.maxCost ? Math.max(existing.maxCost, cost) : cost;
      }
      if (laptop.archived_at) existing.hasArchived = true;
      existing._gpuSet.add(gpuNorm);
      if (existing._gpuSet.size > 1) existing.gpuMixed = true;
    }
  }

  // Strip internal _gpuSet before returning
  return Array.from(groupMap.values()).map(({ _gpuSet: _unused, ...rest }) => rest);
}

/**
 * Formats asking price range for a group.
 */
export function formatGroupPriceRange(minPrice: number, maxPrice: number): string {
  if (minPrice === maxPrice) {
    return `Rs. ${minPrice.toLocaleString('en-PK')}`;
  }
  return `Rs. ${minPrice.toLocaleString('en-PK')} – ${maxPrice.toLocaleString('en-PK')}`;
}
